import React, { useEffect, useRef } from 'react';
import { Button, Flex } from '@chakra-ui/react';
import { ChevronRightIcon } from '@chakra-ui/icons';
import { useGetAvailableSurveysQrCodesQuery, useContinueSurveyMutation } from 'services';
import { useDispatch } from 'react-redux';
import { setActiveSurvey, setCodeType, setSurveyCode, setUserBrandPermissions } from 'store/slices';
import { CodeType } from 'Types/Enums';
import useLocalizedNavigate from 'Utils/useLocalizedNavigate';
import { useLocale } from 'context/LocalizationContext';
import { useDefaultErrorHandler } from 'Utils/errorHandling';
import { getSurveyUrl } from 'Utils/surveyUtils';
import { useLocation } from 'react-router-dom';

interface ITakeSurveyButtonProps {
	productId?: string;
	brandId?: string;
	isMobile?: boolean;
}

export const TakeSurveyButton = ({ productId, brandId, isMobile }: ITakeSurveyButtonProps) => {
	const { locale } = useLocale();
	const navigate = useLocalizedNavigate();
	const location = useLocation();
	const dispatch = useDispatch();
	const hasNavigated = useRef(false);

	const {
		data: qrCodesForAvailableSurveys,
		isError: isAvailableSurveysError,
		error: availableSurveysError,
	} = useGetAvailableSurveysQrCodesQuery({ productId, brandId, locale });

	const [getSurvey, { data: surveyData, isSuccess, isLoading, isError, error }] = useContinueSurveyMutation();

	useDefaultErrorHandler(isError, error);
	useDefaultErrorHandler(isAvailableSurveysError, availableSurveysError);

	const startSurvey = () => {
		if (qrCodesForAvailableSurveys && qrCodesForAvailableSurveys.data.length > 0) {
			getSurvey({ qrCode: qrCodesForAvailableSurveys.data[0], locale });
		}
	};

	useEffect(() => {
		if (isSuccess && surveyData?.data && qrCodesForAvailableSurveys && !hasNavigated.current) {
			hasNavigated.current = true; // Prevent duplicate navigation
			
			const qrCode = qrCodesForAvailableSurveys.data[0];
			dispatch(setActiveSurvey(surveyData.data));
			dispatch(setSurveyCode(qrCode));
			dispatch(setCodeType(CodeType.qrCode));			
			dispatch(setUserBrandPermissions(surveyData?.data.brandPermission));
			setTimeout(() => {
				navigate(getSurveyUrl(surveyData.data.survey, qrCode), {
					state: { from: location.pathname }
				});
			}, 500);
		}
	}, [isSuccess, surveyData, qrCodesForAvailableSurveys, dispatch, navigate]);

	if (!qrCodesForAvailableSurveys || qrCodesForAvailableSurveys.data.length === 0) {
		return null;
	}

	return (
		<Flex
			justifyContent={'center'}
			alignItems={'center'}
			py={4}
		>
			<Button
				w={['350px', 'auto']}
				h={['50px']}
				p={3}
				px={4}
				fontSize={'sm'}
				bg='darkCoral'
				color={'white'}
				border={'1px solid darkCoral'}
				borderRadius={['50px', '5']}
				rightIcon={<ChevronRightIcon />}
				onClick={startSurvey}
				isLoading={isLoading}
				_hover={
					isMobile ? {} : { boxShadow: 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px', transform: 'translateY(1px)' }
				}
			>
				Take survey
			</Button>
		</Flex>
	);
}; 